/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import {
  Children,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useRect } from "~/hooks/useRect";
import { formatPercentage } from "~/utils/helpers";
import { toPx } from "~/utils/components";

import "./ResizeableColumns.css";

import type {
  CSSProperties,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
} from "react";

export interface ResizeableColumnsProps {
  children?: ReactNode;
  style?: CSSProperties;
  firstColumnStyle?: CSSProperties;
  forceSingleColumn?: boolean;
  initPosition?: number;
  minFirstColumnWidth?: number;
  minSecondColumnWidth?: number;
  separatorMargin?: number;
  onPositionUpdate?(position: number): void;
  onColumnReflow?(isSingleColumn: boolean): void; // Notifies container if contents are in single column mode or not.
}

const DEFAULT_SEPARATOR_CURSOR_WIDTH_PX = 1;
const DEFAULT_SEPARATOR_MARGIN_PX = 8;
const DEFAULT_MIN_COLUMN_WIDTH_PX = 50;
const KEYBOARD_MOVE_STEP = 0.025;

export function ResizeableColumns({
  children,
  style,
  firstColumnStyle,
  forceSingleColumn = false,
  initPosition = 0.5,
  separatorMargin,
  minFirstColumnWidth: minFirstColumnWidthPx = DEFAULT_MIN_COLUMN_WIDTH_PX,
  minSecondColumnWidth: minSecondColumnWidthPx = DEFAULT_MIN_COLUMN_WIDTH_PX,
  onPositionUpdate,
  onColumnReflow,
}: ResizeableColumnsProps) {
  const [ref, { width: totalWidthPx }] = useRect<HTMLDivElement>();
  const separatorRef = useRef<HTMLDivElement>(null);

  const [firstColumnWidth, setFirstColumnWidth] = useState(initPosition);

  const [isSeparatorActive, setIsSeparatorActive] = useState(false);

  const [firstChild, secondChild] = useMemo(() => {
    return Children.toArray(children);
  }, [children]);

  const separatorWidth =
    separatorMargin !== undefined
      ? 2 * separatorMargin + DEFAULT_SEPARATOR_CURSOR_WIDTH_PX
      : 2 * DEFAULT_SEPARATOR_MARGIN_PX + DEFAULT_SEPARATOR_CURSOR_WIDTH_PX;

  const shouldUseSingleColumn = useMemo(() => {
    if (forceSingleColumn || !secondChild) return true;

    return (
      totalWidthPx <
      minFirstColumnWidthPx + minSecondColumnWidthPx + separatorWidth
    );
  }, [
    forceSingleColumn,
    minFirstColumnWidthPx,
    minSecondColumnWidthPx,
    secondChild,
    totalWidthPx,
    separatorWidth,
  ]);

  const halfSeparatorPercentage = separatorWidth / 2 / totalWidthPx;

  const firstColumnDivPercentage = firstColumnWidth - halfSeparatorPercentage;

  const secondColumnDivPercentage =
    1 - firstColumnWidth - halfSeparatorPercentage;

  const minFirstColumnWidth = minFirstColumnWidthPx / totalWidthPx;

  const minSecondColumnWidth = minSecondColumnWidthPx / totalWidthPx;

  const maxFirstColumnWidth = 1 - minSecondColumnWidth;

  const ariaValueMin = Math.floor(minFirstColumnWidth * 100);

  const ariaValueMax = Math.floor(maxFirstColumnWidth * 100);

  const ariaValueNow = Math.floor(firstColumnWidth * 100);

  const updateSeparatorPosition = useCallback(
    (position: number) => {
      setFirstColumnWidth(position);
      onPositionUpdate?.(position);
    },
    [onPositionUpdate],
  );

  const clampWidth = useCallback(
    (width: number) => {
      return Math.min(
        maxFirstColumnWidth,
        Math.max(minFirstColumnWidth, width),
      );
    },
    [maxFirstColumnWidth, minFirstColumnWidth],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const separatorRect = separatorRef.current?.getBoundingClientRect();

      if (!isSeparatorActive || !separatorRect) return;

      const mousePastSeparator =
        (e.pageX >= separatorRect.x && e.movementX > 0) ||
        (e.pageX <= separatorRect.x + separatorRect.width && e.movementX < 0);

      if (!mousePastSeparator) return;

      updateSeparatorPosition(
        clampWidth(firstColumnWidth + e.movementX / totalWidthPx),
      );
    },
    [
      isSeparatorActive,
      updateSeparatorPosition,
      clampWidth,
      firstColumnWidth,
      totalWidthPx,
    ],
  );

  const handleMouseUp = useCallback((e: MouseEvent) => {
    setIsSeparatorActive(false);
  }, []);

  const handleMouseOut = useCallback(
    (e: MouseEvent) => {
      if (e.relatedTarget && ref.current?.contains(e.relatedTarget as Node)) {
        return;
      }

      setIsSeparatorActive(false);
    },
    [ref],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowLeft":
          updateSeparatorPosition(
            clampWidth(firstColumnWidth - KEYBOARD_MOVE_STEP),
          );
          break;
        case "ArrowRight":
          updateSeparatorPosition(
            clampWidth(firstColumnWidth + KEYBOARD_MOVE_STEP),
          );
          break;
        case "Home":
          updateSeparatorPosition(
            minFirstColumnWidthPx + halfSeparatorPercentage,
          );
          break;
        case "End":
          updateSeparatorPosition(maxFirstColumnWidth);
          break;
        default:
          // Do nothing
          break;
      }
    },
    [
      clampWidth,
      firstColumnWidth,
      halfSeparatorPercentage,
      maxFirstColumnWidth,
      minFirstColumnWidthPx,
      updateSeparatorPosition,
    ],
  );

  const handleMouseDown = useCallback((e: MouseEvent) => {
    const dividerRect = separatorRef.current?.getBoundingClientRect();

    if (!dividerRect) return;

    if (
      e.clientX >= dividerRect.x &&
      e.clientX <= dividerRect.x + dividerRect.width
    ) {
      setIsSeparatorActive(true);
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  useEffect(() => {
    if (totalWidthPx) updateSeparatorPosition(clampWidth(firstColumnWidth));
  }, [updateSeparatorPosition, clampWidth, firstColumnWidth, totalWidthPx]);

  useEffect(() => {
    onColumnReflow?.(shouldUseSingleColumn);
  }, [onColumnReflow, shouldUseSingleColumn]);

  return (
    <div
      ref={ref}
      className="ResizeableColumns"
      style={style}
      data-force-single-column={shouldUseSingleColumn}
      onPointerDown={shouldUseSingleColumn ? () => {} : handleMouseDown}
      onPointerMove={shouldUseSingleColumn ? () => {} : handleMouseMove}
      onPointerUp={shouldUseSingleColumn ? () => {} : handleMouseUp}
      onPointerOut={shouldUseSingleColumn ? () => {} : handleMouseOut}
    >
      <div
        className="ResizeableColumns__column"
        style={{
          ...firstColumnStyle,
          width: formatPercentage(
            shouldUseSingleColumn ? 1 : firstColumnDivPercentage,
            1,
            false,
          ),
        }}
      >
        {firstChild}
        {shouldUseSingleColumn && secondChild}
      </div>

      {!shouldUseSingleColumn && (
        <div
          role="separator"
          tabIndex={0}
          onKeyDown={handleKeyDown}
          className="ResizeableColumns__separator"
          style={{ "--separator-margin": toPx(separatorMargin) }}
          aria-valuemin={ariaValueMin}
          aria-valuemax={ariaValueMax}
          aria-valuenow={ariaValueNow}
          aria-label="Resizeable Separator"
        >
          <div
            ref={separatorRef}
            className="ResizeableColumns__separator-touch-target"
            data-resizing={isSeparatorActive}
          />
          <div className="ResizeableColumns__separator-cursor"></div>
        </div>
      )}

      {!shouldUseSingleColumn && (
        <div
          className="ResizeableColumns__column"
          style={{
            width: formatPercentage(secondColumnDivPercentage, 1, false),
          }}
        >
          {secondChild}
        </div>
      )}
    </div>
  );
}
